/**
 * Hooks para "compartir tarea personal" (core.personal_task_shares).
 * Una tarea con scope='personal' es privada del owner; sólo se vuelve visible
 * a otros usuarios si el owner la comparte vía email.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as sharesApi from '../api/personalShares';
import { TASK_KEYS } from './keys';

export function useTaskShares(taskId: string | null | undefined) {
  return useQuery({
    queryKey: TASK_KEYS.shares(taskId ?? ''),
    queryFn: () => sharesApi.listSharesForTask(taskId!),
    enabled: !!taskId,
    staleTime: 30_000,
  });
}

export function useShareTaskByEmail(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: Omit<sharesApi.ShareTaskByEmailParams, 'taskId'>) =>
      sharesApi.shareTaskByEmail({ ...params, taskId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASK_KEYS.shares(taskId) });
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      toast.success('Compartido');
    },
    onError: (error: Error) =>
      toast.error('No se pudo compartir', { description: error.message }),
  });
}

export function useRemoveShare(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => sharesApi.removeShare(taskId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASK_KEYS.shares(taskId) });
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      toast.success('Acceso revocado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo revocar', { description: error.message }),
  });
}
